import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, X, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const EMPTY = { title: "", subtitle: "", slug: "", hero_image: "", product_ids: [], order: 0, active: true };

export default function AdminCollections() {
  const { user } = useAuth();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [{ data: cs }, { data: ps }] = await Promise.all([
      api.get("/collections?include_inactive=true"),
      api.get("/products?limit=200"),
    ]);
    setCollections(cs);
    setProducts(ps);
  };
  useEffect(() => { load(); }, []);

  if (!user || user.role !== "admin") {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Admin only.</div>;
  }

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (c) => { setEditing(c.id); setForm({ ...c }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const toggleProduct = (pid) =>
    setForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(pid) ? f.product_ids.filter((x) => x !== pid) : [...f.product_ids, pid],
    }));

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, slug: form.slug || slugify(form.title) };
    try {
      if (editing === "new") {
        await api.post("/admin/collections", payload);
        toast.success("Collection created");
      } else {
        await api.put(`/admin/collections/${editing}`, payload);
        toast.success("Collection updated");
      }
      cancel();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this collection?")) return;
    await api.delete(`/admin/collections/${id}`);
    toast.success("Collection removed");
    load();
  };

  const reorder = async (c, delta) => {
    await api.put(`/admin/collections/${c.id}`, { order: (c.order || 0) + delta });
    load();
  };

  return (
    <div data-testid="admin-collections" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Admin · Editorial</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Featured Collections</h1>
          <p className="text-muted-foreground mt-2 text-sm">Curated landing modules. Shown in order on the homepage.</p>
        </div>
        {!editing && (
          <button onClick={startNew} data-testid="add-collection-btn" className="btn-primary px-5 py-3 text-sm rounded-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New collection
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="border border-border p-8 rounded-sm bg-card mb-10">
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-serif text-2xl">{editing === "new" ? "New collection" : "Edit collection"}</h2>
            <button type="button" onClick={cancel} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Title</label>
              <input data-testid="col-title" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Subtitle</label>
              <textarea data-testid="col-subtitle" rows={2} value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors resize-none" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Slug (URL)</label>
              <input data-testid="col-slug" required pattern="[a-z0-9-]+" value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
              <p className="text-xs text-muted-foreground mt-1">/collections/{form.slug || "your-slug"}</p>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Display order</label>
              <input data-testid="col-order" type="number" value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Hero image URL</label>
              <input data-testid="col-hero" value={form.hero_image}
                onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
                placeholder="https://…"
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="col-active" checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Active (visible to shoppers)
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3 block">
                Products ({form.product_ids.length} selected)
              </label>
              <div className="max-h-80 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                {products.map((p) => {
                  const checked = form.product_ids.includes(p.id);
                  return (
                    <label key={p.id} className={`flex items-center gap-4 p-3 cursor-pointer hover:bg-secondary/50 ${checked ? "bg-primary/5" : ""}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleProduct(p.id)} data-testid={`col-prod-${p.id}`} />
                      <div className="w-12 h-12 bg-secondary overflow-hidden rounded-sm flex-shrink-0">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{p.title}</div>
                        <div className="text-xs text-muted-foreground">{p.category} · ${Number(p.price).toFixed(2)} · {p.seller_name}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mt-8 flex gap-3">
            <button data-testid="save-collection-btn" className="btn-primary px-6 py-3 text-sm rounded-sm">Save collection</button>
            <button type="button" onClick={cancel} className="border border-border px-6 py-3 text-sm rounded-sm hover:bg-secondary transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {!editing && (collections.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground border border-border rounded-sm">
          No collections yet. Create your first one to feature it on the homepage.
        </div>
      ) : (
        <div className="space-y-4">
          {collections.map((c) => (
            <div key={c.id} className="border border-border p-6 rounded-sm bg-card flex gap-6 items-center">
              <div className="w-24 h-24 bg-secondary overflow-hidden rounded-sm flex-shrink-0">
                {c.hero_image && <img src={c.hero_image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="font-serif text-xl">{c.title}</h3>
                  {!c.active && <span className="text-[10px] uppercase tracking-wider border border-border px-2 py-0.5 text-muted-foreground rounded-sm">Hidden</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">/{c.slug} · order {c.order} · {c.products?.length || 0} products</div>
                {c.subtitle && <div className="text-sm text-muted-foreground mt-2 italic">{c.subtitle}</div>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => reorder(c, -1)} className="p-2 hover:bg-secondary rounded-sm" aria-label="Up"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => reorder(c, 1)} className="p-2 hover:bg-secondary rounded-sm" aria-label="Down"><ChevronDown className="w-4 h-4" /></button>
                <Link to={`/collections/${c.slug}`} className="text-xs text-muted-foreground hover:text-primary px-2">View →</Link>
                <button onClick={() => startEdit(c)} className="border border-border px-3 py-2 text-xs hover:bg-secondary rounded-sm inline-flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <button onClick={() => del(c.id)} className="border border-border p-2 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive rounded-sm">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
